import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { settingsService } from '../../services/settingsService';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';

const RolePermissions = () => {
  const [selectedRole, setSelectedRole] = useState('admin');

  const { data, isLoading } = useQuery(
    'roles',
    () => settingsService.getRoles()
  );

  const roles = data?.data || [];
  const selectedRoleData = roles.find(r => r.name === selectedRole);

  // Permission categories
  const permissionCategories = {
    'Member Management': [
      'view_members', 'create_members', 'edit_members', 'delete_members', 'approve_members', 'transfer_members'
    ],
    'Loan Management': [
      'view_loans', 'apply_loans', 'approve_loans', 'disburse_loans', 'collect_repayments', 'reschedule_loans', 'write_off_loans'
    ],
    'Savings Management': [
      'view_savings', 'create_savings', 'deposit_savings', 'withdraw_savings', 'close_savings'
    ],
    'Collection Management': [
      'view_collections', 'submit_collections', 'approve_collections', 'view_collection_reports'
    ],
    'Reports & Analytics': [
      'view_reports', 'export_reports', 'view_dashboard', 'create_custom_reports'
    ],
    'Settings & Admin': [
      'view_settings', 'edit_settings', 'manage_users', 'view_audit_logs', 'manage_backup'
    ]
  };

  const getPermissionLabel = (permission) => {
    return permission.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Role-Based Access Control</h2>
        <p className="text-gray-600">Manage permissions for different user roles</p>
      </div>

      {/* Role Tabs */}
      <div className="flex space-x-2 border-b mb-6">
        {roles.map((role) => (
          <button
            key={role.name}
            onClick={() => setSelectedRole(role.name)}
            className={`px-4 py-2 capitalize ${
              selectedRole === role.name
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {role.name}
          </button>
        ))}
      </div>

      {/* Role Description */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <ShieldCheckIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 capitalize">{selectedRole} Role</h3>
            <p className="text-sm text-blue-600 mt-1">
              {selectedRole === 'admin' && 'Full system access. Can manage all features, users, and settings.'}
              {selectedRole === 'manager' && 'Can manage members, loans, collections, and view reports. Cannot manage system settings.'}
              {selectedRole === 'officer' && 'Can create members, process loans, and submit collections.'}
              {selectedRole === 'field_officer' && 'Can view member details and submit daily collections.'}
              {selectedRole === 'viewer' && 'Read-only access to view members, loans, and reports.'}
            </p>
          </div>
        </div>
      </div>

      {/* Permissions Grid */}
      <div className="space-y-6">
        {Object.entries(permissionCategories).map(([category, permissions]) => (
          <div key={category} className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h3 className="font-semibold">{category}</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {permissions.map((permission) => (
                  <div key={permission} className="flex items-center">
                    <input
                      type="checkbox"
                      id={permission}
                      checked={selectedRoleData?.permissions?.includes(permission) || selectedRole === 'admin'}
                      disabled={selectedRole === 'admin'}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      onChange={() => {}}
                    />
                    <label htmlFor={permission} className="ml-2 text-sm text-gray-700">
                      {getPermissionLabel(permission)}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> Permission changes require the user to log out and log back in to take effect.
          For custom role configurations, please contact system administrator.
        </p>
      </div>
    </div>
  );
};

export default RolePermissions;